import { MouseEventHandler, useMemo } from 'react';
import { css } from '@emotion/react';
import { PhoneNumber } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { PhoneLocationFilterStore, SettingsTenantLocation } from '@frontend/phone-config';
import { formatPhoneNumber, removeNonDigits } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import {
  Chip,
  Text,
  TextLink,
  Truncated,
  TableRowActions,
  Table,
  TableProps,
  SearchField,
  useForm,
} from '@frontend/design-system';
import { trackingId as getTrackingId } from '../../tracking';
import { PhoneSettingsScopedLocationFilterMenu } from '../common/index-table';
import { PhoneOverrideType } from '../override/types';
import { NumbersPanelTabs } from './numbers-panel/call-route-tabs';
import { ExtendedCallRoute } from './types';

type CallRoutesListTableProps = {
  tableData: ExtendedCallRoute[];
  isLoading: boolean;
  settingsTenantLocation: SettingsTenantLocation;
  tableRowActions?: TableRowActions<ExtendedCallRoute> | undefined;
  clickableCallRoutes?: boolean;
  initialSelectedCallRouteId?: string;
  unsavedChangesCallRouteIds?: string[];
  onMoreTextClick?: (tab: NumbersPanelTabs, callRoute?: ExtendedCallRoute) => void;
  onRowSelect?: (callRoute: ExtendedCallRoute) => void;
  filteredLocations: string[];
  fullHeight?: boolean;
  setLocationFilters: PhoneLocationFilterStore['setLocationFilters'];
  routeType?: 'call-routes' | 'device-extension-routes';
  rightToolbarContent?: React.ReactNode;
};

export const CallRoutesListTable = ({
  tableData,
  isLoading,
  tableRowActions,
  settingsTenantLocation,
  clickableCallRoutes = true,
  initialSelectedCallRouteId,
  unsavedChangesCallRouteIds,
  onMoreTextClick,
  onRowSelect,
  filteredLocations,
  fullHeight = true,
  routeType = 'call-routes',
  setLocationFilters,
  rightToolbarContent,
}: CallRoutesListTableProps) => {
  const { t } = useTranslation('phone');
  const { getScopeName } = useAppScopeStore();
  const { navigate: settingsNavigate } = useSettingsNavigate();

  const isUnify = settingsTenantLocation && settingsTenantLocation.locationType === 'unify';

  const form = useForm({
    fields: {
      search: {
        type: 'text',
        debounce: 300,
      },
    },
  });
  const searchValue = form.values.search ?? '';

  /**
   * The table data filtered by the global search input.
   */
  const filteredTableData = useMemo(() => {
    const value = searchValue.trim();
    if (!value || value.length < 3) {
      return tableData;
    }

    return tableData.filter((row) => {
      // Check if there are numbers in the search value since the removeNonDigits function
      // will remove all except numeric chars which can create an empty string if there
      // are no numbers in the string.
      const searchByNumbers = removeNonDigits(value).length > 2;

      // check if found a match in row name first
      if (row.name.toLowerCase().includes(value.toLowerCase())) {
        return true;
      } else if (
        searchByNumbers &&
        row.phoneNumbers?.some((phoneNumber) => String(phoneNumber?.phoneNumber).includes(removeNonDigits(value)))
      ) {
        // check if found a match in phone number
        return true;
      }
      // check if found a match in extensions
      else if (searchByNumbers && String(row.extensionNumbers)?.includes(removeNonDigits(value))) {
        return true;
      }
      return false;
    });
  }, [tableData, filteredLocations, searchValue]);

  const tableConfig: TableProps<ExtendedCallRoute> = useMemo(
    () => ({
      tableInstanceId: 'call-routes-list-table',
      globalTrackingId: 'call-routes-settings-list-table',
      isSelectable: onRowSelect ? true : false,
      uniqueRowId: (row) => row.callRouteId,
      data: filteredTableData ?? [],
      hasResizeColumns: true,
      hasGlobalSearch: false,
      isPaginated: true,
      fullHeight,
      isLoading,
      rowSelectionConfig: {
        isSingleSelect: true,
        onSelectionToggle: (_, data) => onRowSelect?.(data[0]),
        initialState: tableData.reduce((acc, row) => {
          if (row.callRouteId === initialSelectedCallRouteId) {
            return { ...acc, [row.callRouteId]: true };
          }
          return acc;
        }, {}),
      },
      emptyStateConfig: {
        header: t('No call routes'),
        type: 'phone_data',
      },
      colConfig: [
        {
          id: 'name',
          Header: t('Name'),
          accessor: 'name',
          cellRenderer: (name, callRoute) =>
            clickableCallRoutes ? (
              <>
                <Truncated
                  as={TextLink}
                  textAlign='left'
                  css={css`
                    width: fit-content;
                  `}
                  onClick={() => {
                    if (callRoute) {
                      settingsNavigate({
                        to: '/phone/call-routes/:id',
                        params: { id: callRoute.callRouteId },
                      });
                    }
                  }}
                  trackingId={getTrackingId({
                    context: 'setting',
                    feature: routeType,
                    details: 'call-route-name-link',
                  })}
                >
                  {name}
                </Truncated>
                {unsavedChangesCallRouteIds?.includes(callRoute?.callRouteId ?? '') && (
                  <Text
                    size='small'
                    color='subdued'
                    as='span'
                    css={css`
                      font-style: italic;
                      padding-left: ${theme.spacing(1)};
                    `}
                  >
                    {t('Unsaved Changes')}
                  </Text>
                )}
              </>
            ) : (
              <Truncated>{name}</Truncated>
            ),
          sortType: 'string',
          width: 204,
        },
        {
          id: 'phoneNumbers',
          Header: t('Phone Numbers'),
          accessor: 'phoneNumbers',
          cellRenderer: (phoneNumbers: PhoneNumber[], callRoute) => {
            if (!phoneNumbers || phoneNumbers.length === 0) {
              return '--';
            }

            return (
              <TextWithMore
                text={formatPhoneNumber(phoneNumbers[0].phoneNumber)}
                count={phoneNumbers.length}
                onClick={() => onMoreTextClick?.(NumbersPanelTabs.PHONE_NUMBERS, callRoute)}
                trackingId={getTrackingId({
                  context: 'setting',
                  feature: routeType,
                  details: 'more-phone-numbers-btn',
                })}
              />
            );
          },
          width: 236,
          disableSortBy: true,
        },
        {
          id: 'extension',
          Header: t('Extension'),
          accessor: 'extensionNumbers',
          cellRenderer: (extensions, callRoute) => {
            if (!extensions || extensions.length === 0) {
              return '--';
            }

            return (
              <TextWithMore
                text={extensions[0]}
                count={extensions.length}
                onClick={() => onMoreTextClick?.(NumbersPanelTabs.EXTENSIONS, callRoute)}
                trackingId={getTrackingId({
                  context: 'setting',
                  feature: routeType,
                  details: 'more-extensions-btn',
                })}
              />
            );
          },
          width: 138,
          sortType: 'number',
        },
        {
          id: 'locations',
          Header: 'Locations',
          accessor: (data) => {
            const locationIds = [data.locationId];
            if (!locationIds) {
              return;
            }
            if (locationIds.length === 1) {
              return { label: getScopeName(locationIds[0]), type: 'single' };
            } else if (locationIds.length > 1) {
              return { label: t('{{count}} locations', { count: locationIds.length }), type: 'multi' };
            } else {
              return { label: settingsTenantLocation.name, type: 'multi' };
            }
          },
          cellRenderer: (data: { label: string; type: 'multi' | 'single' }) =>
            data.type === 'multi' ? (
              <Chip.MultiChip>{data.label}</Chip.MultiChip>
            ) : (
              <Chip.SingleChip>{data.label}</Chip.SingleChip>
            ),
          width: 150,
          omit: !isUnify,
          sortType: (a, b) => {
            return a.values?.labels?.label.localeCompare(b.values?.labels?.label);
          },
        },
        {
          id: 'overrideStatus',
          Header: t('Override Status'),
          accessor: 'callRoutePhoneOverride',
          cellRenderer: (override?: PhoneOverrideType) => {
            if (!override) {
              return <Chip variant='neutral'>{t('None')}</Chip>;
            } else {
              return override.enabled ? (
                <Chip variant='warn' leftElement={<Icon name='alert-small' />}>
                  {t('Active')}
                </Chip>
              ) : (
                <Chip variant='primaryDark' leftElement={<Icon name='clock-small' />}>
                  {t('Scheduled')}
                </Chip>
              );
            }
          },
          width: 204,
          disableSortBy: true,
        },
      ],
      rowActions: tableRowActions,
    }),
    [
      onRowSelect,
      filteredTableData,
      fullHeight,
      isLoading,
      tableData,
      initialSelectedCallRouteId,
      t,
      clickableCallRoutes,
      settingsNavigate,
      routeType,
      unsavedChangesCallRouteIds,
      onMoreTextClick,
      getScopeName,
      settingsTenantLocation,
      isUnify,
      tableRowActions,
    ]
  );

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(2)};
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: ${isUnify ? 'space-between' : 'flex-end'};
          flex-wrap: wrap;
          gap: ${theme.spacing(2)};
        `}
      >
        {isUnify && (
          <PhoneSettingsScopedLocationFilterMenu
            selectedOptions={filteredLocations}
            onChange={(locations) => {
              setLocationFilters({ callRoutesIndex: locations });
            }}
            trackingIdBase={getTrackingId({ context: 'setting', feature: routeType, details: 'location-filter' })}
          />
        )}
        <div
          css={css`
            display: flex;
            gap: ${theme.spacing(2)};
            align-items: center;
            flex-wrap: wrap;
          `}
        >
          <SearchField
            label={t('Search')}
            {...form.getFieldProps('search')}
            css={css`
              width: 260px;
            `}
            data-trackingid={getTrackingId({ context: 'setting', feature: routeType, details: 'search-input' })}
          />

          <>{rightToolbarContent}</>
        </div>
      </div>

      <Table {...tableConfig} />
    </div>
  );
};

const TextWithMore = ({
  text,
  count,
  onClick,
  trackingId,
}: {
  text: string;
  count: number;
  onClick: MouseEventHandler<HTMLElement>;
  trackingId?: string;
}) => {
  const { t } = useTranslation('phone');
  return (
    <Text size='medium'>
      {text}
      {count > 1 && (
        <TextLink
          css={css`
            margin-left: ${theme.spacing(0.5)};
          `}
          onClick={onClick}
          trackingId={trackingId}
        >
          {t('+{{count}} more', { count: count - 1 })}
        </TextLink>
      )}
    </Text>
  );
};
